<script>
import DefiCard from "@/components/organisms/defi-card";
import Button from "@/components/atoms/button";
import Jauge from "@/components/atoms/jauge";
import Popin from "@/containers/popin-adaptive";
import dateUtils, { dateLib } from "@/utils/date";
import CmsFetcher from "@/containers/cms-fetcher";
import LoadingSpinner from "@/components/molecules/loading-spinner";
import DefisCodes from "@/utils/defis-codes.js";
import Icon from "@/components/atoms/icon";

export default {
  name: "DefiCardOverview",
  components: {
    DefiCard,
    Button,
    Jauge,
    Popin,
    CmsFetcher,
    LoadingSpinner,
    Icon
  },
  props: {
    defi: {
      type: Object,
      required: false,
      default: null
    },
    dateCenter: {
      type: Boolean,
      required: false,
      default: false
    },
    error: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    isAnonyme: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ["adhererAuDefi"],
  data() {
    return {
      popinVisible: false,
      today: dateLib()
    };
  },
  computed: {
    isTermine() {
      return dateLib(dateUtils.toDate(this.defi.attributes.date_fin)).isBefore(
        this.today,
        "day"
      );
    },
    isAvenir() {
      return dateLib(dateUtils.toDate(this.defi.attributes.date_debut)).isAfter(
        this.today,
        "day"
      );
    },
    asAdhesion() {
      return this.defi.attributes.adhesions.length > 0;
    },
    lastAdhesion() {
      return this.asAdhesion && this.defi.attributes.adhesions.slice(-1)[0]
        ? this.defi.attributes.adhesions.slice(-1)[0]
        : null;
    },
    lastAdhesionIsReussi() {
      return (
        this.asAdhesion &&
        this.lastAdhesion.statut === STATUTS_ADHESION_DEFI.termine
      );
    },
    adhesionAvancement() {
      const avancementLimit =
        this.lastAdhesion.niveau_avancement >
        this.defi.attributes.objectif.valeur_max
          ? this.defi.attributes.objectif.valeur_max
          : this.lastAdhesion.niveau_avancement;
      if (this.defi.attributes.objectif.type !== DefisCodes.FREQUENCE) {
        return `${formatPrix(avancementLimit)}€`;
      } else {
        return avancementLimit;
      }
    },
    defiObjectif() {
      if (this.defi.attributes.objectif.type !== DefisCodes.FREQUENCE) {
        return `${formatPrix(this.defi.attributes.objectif.valeur_max)}€`;
      } else {
        return this.defi.attributes.objectif.valeur_max;
      }
    },
    adhesionStatut() {
      if (this.lastAdhesionIsReussi) {
        return "réussi !";
      } else if (this.isTermine) {
        return "terminé";
      } else {
        return "en cours";
      }
    },
    buttonContent() {
      if (this.error || this.loading) {
        return null;
      } else if (this.isAvenir && !this.asAdhesion) {
        return "Je m'inscris à ce défi";
      } else if (!this.asAdhesion && !this.isTermine) {
        return "Je commence ce défi";
      }
      return null;
    }
  }
};
</script>

<template>
  <div>
    <DefiCard
      :defi="defi"
      :date-center="dateCenter"
      :is-termine="isTermine"
      :is-avenir="isAvenir"
      :last-adhesion-is-reussi="lastAdhesionIsReussi"
    >
      <div
        :class="defi.attributes.objectif.type && 'c-defis-card-overview__bottom--reference'"
        class="c-defis-card-overview__bottom"
      >
        <template v-if="asAdhesion && !isAvenir">
          <div class="u-flex-justify-between u-full-width u-flex">
            <p :class="lastAdhesionIsReussi && 'u-color-success u-font-500'">
              Défi {{ adhesionStatut }}
            </p>
            <p>
              <span class="u-font-500">
                {{ adhesionAvancement }}
              </span>
              / {{ defiObjectif }}
            </p>
          </div>
          <Jauge
            :disabled="isTermine && !lastAdhesionIsReussi"
            :max="defi.attributes.objectif.valeur_max"
            :value="defi.attributes.adhesions.slice(-1)[0].niveau_avancement"
          />
        </template>
        <p
          v-if="isTermine && !asAdhesion"
          class="u-font-15 u-flex-self-start u-mt-sm"
        >
          Vous n’avez pas participé à ce défi.
        </p>
        <div class="u-flex u-flex-column u-flex-align-center u-flex-justify-end u-width-full u-flex-grow">
          <Button
            v-if="buttonContent && !isAnonyme"
            size="md"
            radius="full"
            variant="primary"
            class="u-mt-sm40"
            @click="$emit('adhererAuDefi', defi)"
          >
            {{ buttonContent }}
          </Button>
          <LoadingSpinner
            v-if="loading"
            size="sm"
          />
          <p
            v-if="error"
            class="u-color-alert u-font-14"
          >
            Désolé, une erreur s’est produite. Veuillez réessayer plus tard
          </p>
          <p
            v-if="asAdhesion && isAvenir "
            class="u-flex u-flex-align-center u-font-700 u-mt-sm"
          >
            <Icon
              name="check"
              size="sm"
              variant="success"
              class="u-flex-shrink-none u-mr-xxs"
            />
            Vous êtes inscrit(e) pour ce défi
          </p>
          <Button
            v-if="defi.attributes.url_operation"
            class="u-mt-sm"
            link
            @click="popinVisible = true"
          >
            En savoir plus
          </Button>
        </div>
      </div>
    </DefiCard>
    <Popin
      size="lg"
      page
      page-back
      :visible="popinVisible"
      @closed="popinVisible = false"
    >
      <div class="c-defis-card-overview__detail u-flex u-flex-column">
        <DefiCard
          :defi="defi"
          size="full"
          date-center
          detail
          class="u-mt-lg u-mt-none@md u-mx-md@md"
          :is-termine="isTermine"
          :is-avenir="isAvenir"
          :last-adhesion-is-reussi="lastAdhesionIsReussi"
        >
          <div
            v-if="defi.attributes.url_operation"
            class="u-px-sm u-pb-lg20 u-pt-xs"
          >
            <CmsFetcher :link="defi.attributes.url_operation" />
          </div>
        </DefiCard>
        <div class="u-text-center u-mt-md">
          <Button
            size="lg25"
            outline
            radius="full"
            variant="primary"
            @click="popinVisible = false"
          >
            Fermer
          </Button>
        </div>
      </div>
    </Popin>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/defis-card-overview.scss";
</style>
